/**
 * Formatting utility functions
 */

import i18n from 'i18next';

const sizeKeys = ['b', 'kb', 'mb', 'gb', 'tb'] as const;
const bytesKeys = ['bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'] as const;

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return i18n.t('formatting.zeroB', { ns: 'load' });
  
  const k = 1024;
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizeKeys.length - 1);
  const unit = i18n.t(`formatting.${sizeKeys[i]}`, { ns: 'load' });
  
  return new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(bytes / Math.pow(k, i)) + ' ' + unit;
}

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) {
    return i18n.t('formatting.invalidDate', { ns: 'load' });
  }
  
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (days === 0) {
    return i18n.t('formatting.today', { ns: 'load' });
  } else if (days === 1) {
    return i18n.t('formatting.yesterday', { ns: 'load' });
  } else if (days < 7) {
    return i18n.t('formatting.daysAgo', { ns: 'load', count: days });
  } else {
    return d.toLocaleDateString(i18n.language);
  }
}

export function formatDuration(milliseconds: number): string {
  if (milliseconds < 1000) {
    return `${milliseconds}${i18n.t('formatting.milliseconds', { ns: 'load' })}`;
  }
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  const h = i18n.t('formatting.hoursShort', { ns: 'load' });
  const m = i18n.t('formatting.minutesShort', { ns: 'load' });
  const s = i18n.t('formatting.secondsShort', { ns: 'load' });
  
  if (hours > 0) {
    return `${hours}${h} ${minutes % 60}${m}`;
  } else if (minutes > 0) {
    return `${minutes}${m} ${seconds % 60}${s}`;
  } else {
    return `${seconds}${s}`;
  }
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat(i18n.language).format(num);
}

export function formatPercentage(value: number, decimals: number = 1): string {
  return new Intl.NumberFormat(i18n.language, { style: 'percent', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
}

export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return i18n.t('formatting.zeroBytes', { ns: 'load' });

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), bytesKeys.length - 1);
  const unit = i18n.t(`formatting.${bytesKeys[i]}`, { ns: 'load' });

  return new Intl.NumberFormat(i18n.language, { maximumFractionDigits: dm }).format(bytes / Math.pow(k, i)) + ' ' + unit;
}